import {Checkbox, Theme, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import {BookingData, GuestData} from '../helperFunctions';
import {formatDate} from '../../../utils/formatters';
import {Group} from '../../../graphql/groups.datatypes';
import BillingPartySummary from './BillingPartySummary';
import Grid from '@material-ui/core/Grid';
import {allHtmlLinksNewWindow} from '../../../utils/allHtmlLinksNewWindow';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(0),
        paddingBottom: theme.spacing(3),

        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(3),
        },
    },
    padBottom: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    marBottom: {
        marginTop: theme.spacing(3),
        marBottom: theme.spacing(3),
    },
    heading: {
        fontFamily: 'Recoleta-SemiBold',
    },
    body: {
        paddingBottom: theme.spacing(2),
        fontFamily: 'Nunito',
        fontSize: 18,
        letterSpacing: 30 / 1000,

        '& a': {
            color: theme.palette.secondary.main,
            fontWeight: 'bold',

            '& + a': {
                marginLeft: theme.spacing(3),
            },
        },

        '& p': {
            paddingBottom: theme.spacing(2),
        },
        '& ul': {
            color: theme.palette.black,
            listStyle: 'none',
            paddingBottom: theme.spacing(2),

            '& li': {
                paddingBottom: theme.spacing(2),
                '&::before': {
                    content: '"o"',
                    color: theme.palette.primary.main,
                    fontWeight: 'bold',
                    fontSize: 14,
                    marginRight: theme.spacing(1),
                }
            },
        },
        '& h3': {
            paddingBottom: theme.spacing(2),
        },
    },
    policyContainer: {
        height: 'max-content',
    },
    termsLink: {
        color: theme.palette.secondary.main,
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    cancellationLink: {
        color: theme.palette.secondary.main,
        fontWeight: 'bold',
        cursor: 'pointer',
        marginLeft: theme.spacing(3),
    },
    checkbox: {
        marginLeft: -theme.spacing(1.5),
        color: theme.palette.secondary.main,
    },

    datesBlock: {
        fontFamily: 'Nunito',
        fontSize: 18,
        backgroundColor: 'rgba(173,2,163, 0.1)',
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(4),
        padding: 10,
        [theme.breakpoints.down('xs')]: {
            fontSize: 14,
        },

        '.MuiGrid-spacing-xs-1 > & .MuiGrid-item': {
            padding: theme.spacing(1.5),

            [theme.breakpoints.down('xs')]: {
                padding: theme.spacing(1),
            }
        },
    },
    datesBlockCheckIn: {
        borderColor: '#f2f2f2',
        borderStyle: 'solid',
        borderTopWidth: 2,
        borderBottomWidth: 2,
        borderLeftWidth: 0,
        borderRightWidth: 1,
    },
    datesBlockCheckOut: {
        borderColor: '#f2f2f2',
        borderStyle: 'solid',
        borderTopWidth: 2,
        borderBottomWidth: 2,
        borderLeftWidth: 1,
        borderRightWidth: 0,
    },
    datesBlockDisclosure: {
        borderColor: '#f2f2f2',
        borderStyle: 'solid',
        borderWidth: 0,
        borderTopWidth: 2,
    },
    nowrap: {
        whiteSpace: 'nowrap',
    },
    processingMessage: {
        fontSize: 18,
        marginTop: theme.spacing(3),
        color: theme.palette.error.main,
    },
}));

interface Props {
    setActiveStep: (num: number) => void;
    group: Group;
    bookingData: BookingData;
    setBookingData: (data: BookingData) => void;
    allGuestData: GuestData[];
    adminVersion?: boolean;
}

const Step5Summary = (props: Props) => {
    const classes = useStyles();
    const {allGuestData, bookingData, group, adminVersion, setBookingData} = props;
    const [confirmSelected, setConfirmSelected] = useState({
        termsAndCondition: !!adminVersion,
        cancellationPolicy: !!adminVersion,
    });

    useEffect(() => {
        const confirmed = Object.values(confirmSelected).every(Boolean);
        const validData = bookingData.validData.map((data, i) => i === 5 ? confirmed : data);
        setBookingData({...bookingData, validData});
    }, [confirmSelected]);

    const insuranceCopy = group.description_insurance && allHtmlLinksNewWindow(group.description_insurance);

    const bookingLength = bookingData.nights.end.diff(bookingData.nights.start, 'days');

    return (
        <div className={classes.root}>

        <Grid item xs={12}>
            <Typography variant="h3" className={clsx(classes.heading)}>My
                Stay</Typography>
        </Grid>

        <Grid item xs={12} className={clsx(classes.datesBlock)}>
            <Grid container spacing={1}>
                <Grid item xs={12} className={clsx([classes.nowrap])}>
                    <strong>Event:</strong> {group.tour!.display_name}
                </Grid>
                <Grid item xs={12} className={clsx([classes.nowrap, classes.datesBlockDisclosure])}>
                    <strong>Group:</strong> {group.name}
                </Grid>
                <Grid item xs={12} className={clsx([classes.nowrap, classes.datesBlockDisclosure])}>
                    <strong>Hotel:</strong> {group.hotel!.display_name}
                </Grid>
                <Grid item xs={6} className={clsx([classes.nowrap, classes.datesBlockCheckIn])}>
                    <strong>Check In:</strong> {formatDate(bookingData.nights.start)}
                </Grid>
                <Grid item xs={6} className={clsx([classes.nowrap, classes.datesBlockCheckOut])}>
                    <strong>Check Out:</strong> {formatDate(bookingData.nights.end)}
                </Grid>
                <Grid item xs={12} className={classes.nowrap}>
                    <strong>Duration:</strong> {bookingLength}-Nights
                </Grid>
                <Grid item xs={12} className={classes.datesBlockDisclosure}>
                    <strong>Arrival/Departure Information:</strong><br/>
                    {group.description_checkin}
                </Grid>
            </Grid>
        </Grid>


            <Typography variant="h1" className={clsx([classes.heading])}>
                Payment Summary
            </Typography>



            {bookingData.billingPartiesData.map((billingParty, billingPartyIndex) => {
                return (
                    <BillingPartySummary
                        key={billingPartyIndex}
                        billingPartyIndex={billingPartyIndex}
                        billingParty={billingParty}
                        allGuestData={allGuestData}
                        group={group}
                        bookingData={bookingData}
                    />

                );
            })}
            <br/><br/>

            {!adminVersion && (
                <Grid container>
                    <Grid item xs={12}>
                        <Typography variant="body1" className={classes.body}
                            dangerouslySetInnerHTML={{__html: group.payment_schedule!.description}}
                        />
                        <Grid item xs={12} style={{marginBottom: '30px'}}>
                            <Typography variant="h3" style={{marginBottom: '12px'}}>
                                Terms & Conditions
                            </Typography>
                            <div className={clsx([classes.body, classes.policyContainer])}>
                                <Typography
                                    variant="body1"
                                    className={classes.body}
                                    dangerouslySetInnerHTML={{__html: group.tour!.terms_and_conditions || ''}}
                                />
                                <Typography component="label" variant="body1" className={clsx([classes.body, classes.marBottom])}>
                                    <Checkbox className={classes.checkbox} onChange={(event, checked) => setConfirmSelected(prev => ({...prev, termsAndCondition: checked}))} />
                                    I have read and agree to all Terms & Conditions.
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{marginBottom: '30px'}}>
                            <Typography variant="h3" style={{marginBottom: '12px'}}>
                                Cancellation Policy
                            </Typography>
                            <div className={clsx([classes.body, classes.policyContainer])}>
                                <Typography
                                    variant="body1"
                                    className={classes.body}
                                    dangerouslySetInnerHTML={{__html: group.tour!.cancellation_policy || ''}}
                                />
                                <Typography component="label" variant="body1" className={clsx([classes.body, classes.marBottom])}>
                                    <Checkbox className={classes.checkbox} onChange={(event, checked) => setConfirmSelected(prev => ({...prev, cancellationPolicy: checked}))} />
                                    I have read and agree to the Cancellation Policy.
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{marginBottom: '30px'}}>
                            <Typography variant="h3" style={{marginBottom: '12px'}}>
                                Optional Travel Insurance
                            </Typography>
                            <div className={clsx([classes.body, classes.policyContainer])}>
                                <Typography
                                    variant="body1"
                                    className={classes.body}
                                    dangerouslySetInnerHTML={{__html: insuranceCopy || ''}}
                                />
                            </div>
                        </Grid>
                        <Typography variant="body1" className={clsx([classes.body, classes.processingMessage])}>
                            PLEASE be patient and only press the confirm button “one time”.  It may take several minutes to process or depending on your internet connection you may receive a “booking fail error”.  Once you receive a “confirm” screen or “booking failure error” please check your email (The reservation will still process on the server). A verification email will be sent to your email with login instructions to gain access to your account for future access.
                        </Typography>
                    </Grid>
                </Grid>
            )}
        </div>
    );
};

export default Step5Summary;
